/** The first-run list: a title and at most five lines, each a label with a state and a link -
 * ADR-0212 decision 2 ("the first-run empty state is a short checklist, not paragraphs").
 *
 * **It is the page's content, not a note inside an empty table** (KD, 2026-11-02): a card of numbered
 * steps in the order a person does the work, each a few words with where it stands, and the step to do
 * now drawn larger - an accent ring, a filled number and the page's primary button. A step already done
 * keeps its place with a tick. A line is a label of a few words (checked in development against the
 * same four-word ceiling a tile has, plus two), a state and optionally the way to do it, built by the
 * caller; a sixth line throws, because a list that grows past five is a form wearing a checklist's name.
 *
 * State is a glyph and a word, never colour alone. Which line is next is the caller's walk (`phase`):
 * the first that is not done.
 */

import { CheckCircle, WarningCircle } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { StatusChip } from "./StatusChip";
import styles from "./Checklist.module.css";
import { LABEL_MAX_WORDS, labelWords } from "./Tile";

export const CHECKLIST_MAX_LINES = 5;

export type ChecklistState = "done" | "todo" | "blocked";

export interface ChecklistItem {
  id: string;
  label: string;
  /** The label as a link to where the step is done, built by the caller (a router `Link` around
   * `label`). Absent for a role that may not do the step: the words alone, no control. */
  labelLink?: ReactNode;
  state: ChecklistState;
  /** The way to do it: a router `Link` or a button, built by the caller. The one to do now carries
   * the primary one. */
  action?: ReactNode;
  /** Where the line stands in the caller's own walk (`done`, `next`, `waiting`, `unchecked`): three of
   * those are all "to do" to a reader, and a screen test or the browser gate can tell them apart. The
   * line the walk calls `next` is the one drawn larger. */
  phase?: string;
}

const TONE = { done: "ok", todo: "neutral", blocked: "warn" } as const;
const WORD = { done: "Done", todo: "To do", blocked: "Blocked" } as const;

export function Checklist({
  title,
  items,
  testId = "checklist",
}: {
  title: string;
  items: readonly ChecklistItem[];
  testId?: string;
}) {
  if (import.meta.env.DEV) {
    if (items.length > CHECKLIST_MAX_LINES) {
      throw new Error(
        `A checklist has at most ${String(CHECKLIST_MAX_LINES)} lines (ADR-0212); this one has ${String(items.length)}.`,
      );
    }
    for (const item of items) {
      if (labelWords(item.label) > LABEL_MAX_WORDS + 2) {
        throw new Error(`Checklist line "${item.label}" is a sentence; a line is a few words.`);
      }
    }
  }
  return (
    <section className={`card ${styles.card}`} aria-label={title} data-testid={testId}>
      <h2 className={styles.title}>{title}</h2>
      <ol className={styles.list}>
        {items.map((item, at) => (
          <li
            key={item.id}
            className={styles.line}
            data-state={item.state}
            data-step={item.id}
            data-phase={item.phase}
            data-testid={`${testId}-step`}
          >
            <span className={styles.mark} aria-hidden="true">
              {item.state === "done" ? (
                <CheckCircle size={22} weight="fill" />
              ) : item.state === "blocked" ? (
                <WarningCircle size={22} weight="fill" />
              ) : (
                <span className={styles.number}>{at + 1}</span>
              )}
            </span>
            <span className={styles.label}>{item.labelLink ?? item.label}</span>
            {/* The state is a chip, as every status is (ADR-0212): a word on a line is a label, not body text. */}
            <StatusChip tone={TONE[item.state]} size="sm">
              {WORD[item.state]}
            </StatusChip>
            <span className={styles.action}>{item.action}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}
